import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, DatePicker, Radio,
  Tag, Space, Typography, message, Row, Col, Progress, Divider,
  Popconfirm, Select, Alert, Upload, Tooltip, Switch
} from 'antd';
import {
  FileTextOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
  CopyOutlined, EyeOutlined, CheckCircleOutlined, ClockCircleOutlined,
  UploadOutlined, TrophyOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  fetchModuleAssessments, fetchAssessmentDetails, saveAssessment,
  deleteAssessment, submitAssessmentAttempt, fetchAssessmentResults,
  copyAssessment, uploadQuestionImage
} from '../../../../Service/Assessment_API/assessmentApi';
import { fetchCourses } from '../../../../Service/Courses_API/coursesApi';
import { fetchModules } from '../../../../Service/Modules_API/modulesApi';
import { SERVER_URL } from '../../../../Service/apiConfig';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const AssessmentTab = ({ course, module, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  
  // Quiz Builder Modal
  const [builderVisible, setBuilderVisible] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [builderForm] = Form.useForm();
  const [questions, setQuestions] = useState([]);

  // Quiz Player Modal
  const [playerVisible, setPlayerVisible] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  // Results Panel Modal (Admin / Studio / User)
  const [resultsVisible, setResultsVisible] = useState(false);
  const [selectedAssessmentForResults, setSelectedAssessmentForResults] = useState(null);
  const [resultsList, setResultsList] = useState([]);
  const [resultsSearchText, setResultsSearchText] = useState('');
  const [loadingResults, setLoadingResults] = useState(false);

  // Copy Assessment Modal (Admin / Studio)
  const [copyVisible, setCopyVisible] = useState(false);
  const [assessmentToCopy, setAssessmentToCopy] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [targetCourseId, setTargetCourseId] = useState(null);
  const [targetModuleId, setTargetModuleId] = useState(null);
  const [copying, setCopying] = useState(false);

  const isAdminOrStudio = userRole === 'ADMIN' || userRole === 'STUDIO';

  const loadAssessments = useCallback(async () => {
    if (!module?.modules_id) return;
    setLoading(true);
    try {
      const data = await fetchModuleAssessments(module.modules_id);
      if (data.success) {
        setAssessments(data.assessments || []);
      }
    } catch (err) {
      console.error('Failed to load assessments:', err);
      message.error('Failed to load module assessments');
    } finally {
      setLoading(false);
    }
  }, [module?.modules_id]);

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  // Handle Quiz Timer Countdown & Auto-Submit
  useEffect(() => {
    if (!playerVisible || quizResult) return;

    // Auto-submit if scheduled end time is reached
    if (activeQuiz?.end_time) {
      const scheduledEnd = dayjs(activeQuiz.end_time);
      if (dayjs().isAfter(scheduledEnd)) {
        message.warning('Scheduled assessment end time reached! Submitting quiz automatically...');
        handleAutoSubmit();
        return;
      }
    }

    if (timeLeftSeconds === null || timeLeftSeconds <= 0) return;

    const timer = setInterval(() => {
      // Check scheduled end time during interval
      if (activeQuiz?.end_time && dayjs().isAfter(dayjs(activeQuiz.end_time))) {
        clearInterval(timer);
        message.warning('Scheduled assessment end time reached! Submitting quiz automatically...');
        handleAutoSubmit();
        return;
      }

      setTimeLeftSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          message.warning('Time limit reached! Submitting quiz automatically...');
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeftSeconds, playerVisible, quizResult, activeQuiz]);

  // Open Builder Modal
  const handleOpenBuilder = async (record = null) => {
    setEditingAssessment(record);
    builderForm.resetFields();
    if (record) {
      try {
        setLoading(true);
        const details = await fetchAssessmentDetails(record.assessment_id);
        const a = details.assessment;
        builderForm.setFieldsValue({
          title: a.title,
          description: a.description,
          duration_minutes: a.duration_minutes || 0,
          allow_multiple_attempts: Boolean(a.allow_multiple_attempts),
          date_range: (a.start_time && a.end_time) ? [dayjs(a.start_time), dayjs(a.end_time)] : null
        });
        setQuestions(details.questions || []);
      } catch (err) {
        message.error('Failed to load quiz details for editing');
      } finally {
        setLoading(false);
      }
    } else {
      setQuestions([
        {
          question_text: '',
          question_image: '',
          options: ['', '', '', ''],
          correct_answer: '0',
          points: 1,
          negative_points: 0
        }
      ]);
    }
    setBuilderVisible(true);
  };

  // Add question row in builder
  const handleAddQuestion = () => {
    setQuestions(prev => [
      ...prev,
      {
        question_text: '',
        question_image: '',
        options: ['', '', '', ''],
        correct_answer: '0',
        points: 1,
        negative_points: 0
      }
    ]);
  };

  // Remove question row
  const handleRemoveQuestion = (idx) => {
    if (questions.length === 1) {
      message.warning('Assessment must contain at least one question');
      return;
    }
    setQuestions(prev => prev.filter((_, i) => i !== idx));
  };

  // Update Question Field
  const handleUpdateQuestion = (idx, key, val) => {
    setQuestions(prev => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: val };
      return copy;
    });
  };

  // Update Question Option Text
  const handleUpdateOption = (qIdx, optIdx, val) => {
    setQuestions(prev => {
      const copy = [...prev];
      const opts = [...(copy[qIdx].options || ['', '', '', ''])];
      opts[optIdx] = val;
      copy[qIdx] = { ...copy[qIdx], options: opts };
      return copy;
    });
  };

  // Upload Question Image
  const handleImageUpload = async (qIdx, file) => {
    try {
      const res = await uploadQuestionImage(file);
      if (res.success) {
        handleUpdateQuestion(qIdx, 'question_image', res.image_url);
        message.success('Question image uploaded successfully');
      }
    } catch (err) {
      message.error(err.message || 'Image upload failed');
    }
    return false; // prevent default upload action
  };

  // Save Assessment (Admin / Studio)
  const handleSaveAssessment = async () => {
    try {
      const values = await builderForm.validateFields();
      if (questions.length === 0) {
        message.error('Please add at least one question');
        return;
      }

      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        if (!q.question_text && !q.question_image) {
          message.error(`Question #${i + 1} must have either text or an image.`);
          return;
        }
      }

      const dateRange = values.date_range;
      const startTime = dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD HH:mm:ss') : null;
      const endTime = dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD HH:mm:ss') : null;

      const payload = {
        assessment_id: editingAssessment?.assessment_id,
        courses_id: course?.courses_id,
        modules_id: module?.modules_id,
        title: values.title,
        description: values.description,
        duration_minutes: values.duration_minutes || 0,
        allow_multiple_attempts: values.allow_multiple_attempts ? 1 : 0,
        start_time: startTime,
        end_time: endTime,
        questions
      };

      setLoading(true);
      const res = await saveAssessment(payload);
      if (res.success) {
        message.success(res.message);
        setBuilderVisible(false);
        loadAssessments();
      }
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message || 'Failed to save assessment');
    } finally {
      setLoading(false);
    }
  };

  // Delete Assessment
  const handleDeleteAssessment = async (id) => {
    try {
      setLoading(true);
      const res = await deleteAssessment(id);
      if (res.success) {
        message.success('Assessment deleted');
        loadAssessments();
      }
    } catch (err) {
      message.error('Failed to delete assessment');
    } finally {
      setLoading(false);
    }
  };

  // Open Quiz Player for Student / User
  const handleStartQuiz = async (record) => {
    const now = dayjs();
    const start = record.start_time ? dayjs(record.start_time) : null;
    const end = record.end_time ? dayjs(record.end_time) : null;

    if (record.my_attempts_count > 0 && !record.allow_multiple_attempts) {
      message.warning('You have already submitted this assessment. Multiple attempts are not allowed.');
      return;
    }

    if (start && now.isBefore(start)) {
      message.warning(`Assessment has not started yet. Starts at ${start.format('DD MMM YYYY, hh:mm A')}`);
      return;
    }

    if (end && now.isAfter(end)) {
      message.warning(`Assessment schedule has expired. Ended at ${end.format('DD MMM YYYY, hh:mm A')}`);
      return;
    }

    try {
      setLoading(true);
      const details = await fetchAssessmentDetails(record.assessment_id);
      const assessmentData = details.assessment || record;
      setActiveQuiz(assessmentData);
      setQuizQuestions(details.questions || []);
      setCurrentQIndex(0);
      setUserAnswers({});
      setQuizResult(null);

      const durMinutes = assessmentData?.duration_minutes || 0;
      let calculatedSeconds = null;

      if (durMinutes > 0) {
        calculatedSeconds = durMinutes * 60;
      }

      const schEnd = assessmentData?.end_time ? dayjs(assessmentData.end_time) : end;
      if (schEnd) {
        const secondsUntilEnd = Math.max(0, schEnd.diff(now, 'second'));
        if (calculatedSeconds !== null) {
          calculatedSeconds = Math.min(calculatedSeconds, secondsUntilEnd);
        } else {
          calculatedSeconds = secondsUntilEnd;
        }
      }

      setTimeLeftSeconds(calculatedSeconds);
      setPlayerVisible(true);
    } catch (err) {
      message.error('Failed to load quiz details');
    } finally {
      setLoading(false);
    }
  };

  // Submit Quiz Attempt
  const handleFinalSubmitQuiz = async () => {
    if (!activeQuiz) return;
    setSubmittingQuiz(true);
    try {
      const res = await submitAssessmentAttempt(activeQuiz.assessment_id, userAnswers);
      if (res.success) {
        setQuizResult(res.result);
        message.success('Quiz submitted! Check your score below.');
        loadAssessments();
      }
    } catch (err) {
      message.error(err.message || 'Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleAutoSubmit = () => {
    handleFinalSubmitQuiz();
  };

  // View Results Panel (Admin / Studio)
  const handleOpenResults = async (record) => {
    setSelectedAssessmentForResults(record);
    setResultsSearchText('');
    setResultsVisible(true);
    setLoadingResults(true);
    try {
      const res = await fetchAssessmentResults(record.assessment_id);
      if (res.success) {
        setResultsList(res.attempts || []);
      }
    } catch (err) {
      message.error('Failed to fetch assessment results');
    } finally {
      setLoadingResults(false);
    }
  };

  // Open Copy Assessment Modal
  const handleOpenCopy = async (record) => {
    setAssessmentToCopy(record);
    setCopyVisible(true);
    setTargetCourseId(course?.courses_id || null);
    setTargetModuleId(null);
    try {
      const cRes = await fetchCourses();
      const courseArr = Array.isArray(cRes) ? cRes : (cRes.courses || []);
      setAllCourses(courseArr);
      if (course?.courses_id) {
        const mRes = await fetchModules(course.courses_id);
        setAllModules(Array.isArray(mRes) ? mRes : (mRes.modules || []));
      }
    } catch (err) {
      message.error('Failed to fetch courses/modules for copy dropdown');
    }
  };

  const handleCourseChangeForCopy = async (cId) => {
    setTargetCourseId(cId);
    setTargetModuleId(null);
    setAllModules([]);
    if (cId) {
      try {
        const mRes = await fetchModules(cId);
        setAllModules(Array.isArray(mRes) ? mRes : (mRes.modules || []));
      } catch (err) {
        message.error('Failed to fetch modules for target course');
      }
    }
  };

  const handleExecuteCopy = async () => {
    if (!targetCourseId || !targetModuleId) {
      message.error('Please select both target course and target module.');
      return;
    }
    setCopying(true);
    try {
      const res = await copyAssessment(assessmentToCopy.assessment_id, targetCourseId, targetModuleId);
      if (res.success) {
        message.success(res.message);
        setCopyVisible(false);
        if (targetModuleId === module?.modules_id) {
          loadAssessments();
        }
      }
    } catch (err) {
      message.error(err.message || 'Failed to copy assessment');
    } finally {
      setCopying(false);
    }
  };

  // Helper formatting for full image URLs
  const getFullImageUrl = (imgUrl) => {
    if (!imgUrl) return '';
    if (imgUrl.startsWith('http')) return imgUrl;
    return `${SERVER_URL}${imgUrl.startsWith('/') ? '' : '/'}${imgUrl}`;
  };

  // Helper for Quiz Status Tag
  const getStatusTag = (record) => {
    const now = dayjs();
    const start = record.start_time ? dayjs(record.start_time) : null;
    const end = record.end_time ? dayjs(record.end_time) : null;

    if (start && now.isBefore(start)) {
      return <Tag color="orange">📅 Scheduled ({start.format('DD MMM, hh:mm A')})</Tag>;
    }
    if (end && now.isAfter(end)) {
      return <Tag color="gray">🔒 Expired ({end.format('DD MMM, hh:mm A')})</Tag>;
    }
    return <Tag color="green">🟢 Active Now</Tag>;
  };

  // Columns for Main Assessment Table
  const tableColumns = [
    {
      title: 'Assessment Title',
      dataIndex: 'title',
      key: 'title',
      render: (text, rec) => (
        <div>
          <strong style={{ fontSize: 14, color: 'var(--text-color)' }}>{text}</strong>
          {rec.description && (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{rec.description}</div>
          )}
        </div>
      )
    },
    {
      title: 'Questions',
      dataIndex: 'total_questions',
      key: 'total_questions',
      render: (count) => <Tag color="purple"><FileTextOutlined /> {count || 0} Questions</Tag>
    },
    {
      title: 'Duration',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      render: (mins) => mins > 0 ? `${mins} mins` : <Tag color="blue">No Time Limit</Tag>
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, rec) => getStatusTag(rec)
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, rec) => {
        const now = dayjs();
        const start = rec.start_time ? dayjs(rec.start_time) : null;
        const end = rec.end_time ? dayjs(rec.end_time) : null;

        const isNotStarted = start && now.isBefore(start);
        const isExpired = end && now.isAfter(end);
        const hasAttempted = rec.my_attempts_count > 0;
        const allowMultiple = Boolean(rec.allow_multiple_attempts);

        const isTestDisabled = isNotStarted || isExpired || (hasAttempted && !allowMultiple);

        let tooltipTitle = '';
        if (isNotStarted) {
          tooltipTitle = `Assessment starts at ${start.format('DD MMM YYYY, hh:mm A')}`;
        } else if (isExpired) {
          tooltipTitle = `Assessment ended at ${end.format('DD MMM YYYY, hh:mm A')}`;
        } else if (hasAttempted && !allowMultiple) {
          tooltipTitle = 'Single attempt completed. Multiple attempts are disabled.';
        }

        return (
          <Space size="small">
            {hasAttempted && (
              <Button
                type="default"
                icon={<EyeOutlined />}
                onClick={() => handleOpenResults(rec)}
              >
                View My Result
              </Button>
            )}

            {(!hasAttempted || allowMultiple) && (
              <Tooltip title={tooltipTitle}>
                <span>
                  <Button
                    type="primary"
                    icon={<FileTextOutlined />}
                    disabled={isTestDisabled}
                    onClick={() => handleStartQuiz(rec)}
                  >
                    {isNotStarted
                      ? 'Not Started'
                      : isExpired
                      ? 'Expired'
                      : hasAttempted
                      ? 'Retake Test'
                      : 'Take Test'}
                  </Button>
                </span>
              </Tooltip>
            )}

            {isAdminOrStudio && (
              <>
                <Button
                  icon={<EyeOutlined />}
                  onClick={() => handleOpenResults(rec)}
                >
                  All Results
                </Button>
                <Button
                  icon={<CopyOutlined />}
                  onClick={() => handleOpenCopy(rec)}
                >
                  Copy
                </Button>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => handleOpenBuilder(rec)}
                />
                <Popconfirm
                  title="Delete this assessment?"
                  onConfirm={() => handleDeleteAssessment(rec.assessment_id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </>
            )}
          </Space>
        );
      }
    }
];

  // Columns for Results Table
  const resultsColumns = [
    {
      title: 'Student Name',
      key: 'student_info',
      render: (_, rec) => {
        const fullName = rec.full_name || rec.student_full_name || rec.login_name || rec.login_username || 'Student';
        const username = rec.login_username;
        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-color, #1e293b)' }}>
              {fullName}
            </div>
            {username && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary, #64748b)', marginTop: 2 }}>
                @{username}
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Attempt Date',
      dataIndex: 'attempted_at',
      key: 'attempted_at',
      render: (dt) => dayjs(dt).format('DD MMM YYYY, hh:mm A')
    },
    {
      title: 'Score',
      key: 'score',
      render: (_, rec) => `${rec.correct_count} / ${rec.total_questions}`
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (pct) => (
        <Tag color={pct >= 50 ? 'green' : 'red'} style={{ fontWeight: 700 }}>
          {pct}%
        </Tag>
      )
    }
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card
        title={
          <Space>
            <FileTextOutlined style={{ fontSize: 20, color: '#3b82f6' }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>Module Assessments & Quizzes</span>
          </Space>
        }
        extra={
          isAdminOrStudio && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => handleOpenBuilder(null)}
            >
              Create Quiz / Assessment
            </Button>
          )
        }
        style={{ borderRadius: 12, border: '1px solid var(--border-color)' }}
      >
        <Table
          dataSource={assessments}
          columns={tableColumns}
          rowKey="assessment_id"
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          locale={{ emptyText: 'No assessments created for this module yet.' }}
        />
      </Card>

      {/* QUIZ BUILDER MODAL (ADMIN / STUDIO) */}
      <Modal
        title={editingAssessment ? 'Edit Assessment' : 'Create New Assessment'}
        open={builderVisible}
        onCancel={() => setBuilderVisible(false)}
        onOk={handleSaveAssessment}
        confirmLoading={loading}
        width={1000}
        okText="Save Assessment"
      >
        <Form form={builderForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title" label="Assessment Title" rules={[{ required: true, message: 'Please enter title' }]}>
                <Input placeholder="e.g. Chapter 1 Chapter Quiz" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="duration_minutes" label="Duration (Minutes)">
                <InputNumber min={0} max={180} style={{ width: '100%' }} placeholder="0 = No limit" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="allow_multiple_attempts" valuePropName="checked" label="Attempt Policy">
                <Switch checkedChildren="Multiple Attempts" unCheckedChildren="Single Attempt Only" style={{ marginTop: 4 }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Instructions / Description">
            <Input.TextArea rows={2} placeholder="Optional instructions for students..." />
          </Form.Item>

          <Form.Item name="date_range" label="Schedule Duration (Optional - Leave blank to allow all candidates to take self-assessment anytime)">
            <DatePicker.RangePicker showTime={{ use12Hours: true, format: 'hh:mm A' }} format="YYYY-MM-DD hh:mm A" style={{ width: '100%' }} />
          </Form.Item>

          <Divider align="left">
            <Text strong>Questions & Multiple Choice Options ({questions.length})</Text>
          </Divider>

          {questions.map((q, qIdx) => (
            <Card
              key={qIdx}
              size="small"
              title={<Text strong>Question #{qIdx + 1}</Text>}
              extra={
                <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleRemoveQuestion(qIdx)}>
                  Remove
                </Button>
              }
              style={{ marginBottom: 16, background: 'var(--panel-color, #fafafa)', borderColor: '#e2e8f0' }}
            >
              <Form.Item label={q.question_image ? "Question Text (Optional)" : "Question Text"} required={!q.question_image}>
                <Input.TextArea
                  rows={2}
                  value={q.question_text}
                  onChange={(e) => handleUpdateQuestion(qIdx, 'question_text', e.target.value)}
                  placeholder="Type your question text here..."
                />
              </Form.Item>

              <Form.Item label="Question Image (Optional)">
                <Space style={{ marginBottom: 8 }}>
                  <Upload
                    beforeUpload={(file) => handleImageUpload(qIdx, file)}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />}>Upload Question Image</Button>
                  </Upload>
                  {q.question_image && (
                    <Tag color="blue" closable onClose={() => handleUpdateQuestion(qIdx, 'question_image', '')}>
                      Image Uploaded
                    </Tag>
                  )}
                </Space>
                {q.question_image && (
                  <div style={{ marginTop: 8 }}>
                    <img
                      src={getFullImageUrl(q.question_image)}
                      alt="Question Preview"
                      style={{ maxHeight: 120, borderRadius: 8, border: '1px solid #cbd5e1' }}
                    />
                  </div>
                )}
              </Form.Item>

              <Row gutter={16} style={{ marginBottom: 12 }}>
                <Col span={12}>
                  <Text strong style={{ fontSize: 13, color: '#10b981' }}>+ Marks if Correct:</Text>
                  <InputNumber
                    min={0}
                    step={1}
                    precision={0}
                    value={Math.round(q.points ?? 1)}
                    onChange={(val) => handleUpdateQuestion(qIdx, 'points', val)}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Col>
                <Col span={12}>
                  <Text strong style={{ fontSize: 13, color: '#ef4444' }}>- Negative Marks if Wrong:</Text>
                  <InputNumber
                    min={0}
                    step={1}
                    precision={0}
                    value={Math.round(q.negative_points ?? 0)}
                    onChange={(val) => handleUpdateQuestion(qIdx, 'negative_points', val)}
                    style={{ width: '100%', marginTop: 4 }}
                  />
                </Col>
              </Row>

              <Text strong style={{ fontSize: 13 }}>Multiple Choice Options (Optional) & Select Correct Answer:</Text>
              <Radio.Group
                style={{ width: '100%', marginTop: 8 }}
                value={String(q.correct_answer)}
                onChange={(e) => handleUpdateQuestion(qIdx, 'correct_answer', e.target.value)}
              >
                <Row gutter={[16, 16]}>
                  {(q.options || ['', '', '', '']).map((optText, optIdx) => (
                    <Col span={12} key={optIdx}>
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Radio value={String(optIdx)} style={{ marginRight: 8 }}>
                          <strong style={{ fontSize: 13 }}>Option {String.fromCharCode(65 + optIdx)}:</strong>
                        </Radio>
                        <Input
                          value={optText}
                          onChange={(e) => handleUpdateOption(qIdx, optIdx, e.target.value)}
                          placeholder={`Option ${String.fromCharCode(65 + optIdx)} text (Optional)`}
                          style={{ flex: 1 }}
                        />
                      </div>
                    </Col>
                  ))}
                </Row>
              </Radio.Group>
            </Card>
          ))}

          <Button type="dashed" block icon={<PlusOutlined />} onClick={handleAddQuestion}>
            Add Question
          </Button>
        </Form>
      </Modal>

      {/* INTERACTIVE QUIZ PLAYER MODAL */}
      <Modal
        title={
          <Space>
            <TrophyOutlined style={{ color: '#f59e0b', fontSize: 20 }} />
            <span>{activeQuiz?.title}</span>
          </Space>
        }
        open={playerVisible}
        onCancel={() => setPlayerVisible(false)}
        footer={null}
        width={950}
        maskClosable={false}
      >
        {quizResult ? (
          /* RESULT SCREEN */
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Progress
              type="circle"
              percent={quizResult.percentage}
              format={(percent) => `${percent}%`}
              strokeColor={quizResult.percentage >= 50 ? '#10b981' : '#ef4444'}
            />
            <Title level={3} style={{ marginTop: 16 }}>
              {quizResult.percentage >= 50 ? '🎉 Congratulations!' : '📚 Keep Learning!'}
            </Title>
            <Paragraph style={{ fontSize: 16 }}>
              Total Net Score: <strong style={{ color: '#10b981' }}>{Math.round(quizResult.score || 0)}</strong> / {Math.round(quizResult.max_possible_points || quizResult.total_questions || 0)} Marks
              <br />
              <Text type="secondary" style={{ fontSize: 13 }}>
                (Correct: {quizResult.correct_count}, Incorrect: {quizResult.wrong_count || 0})
              </Text>
            </Paragraph>

            <Divider align="left">Detailed Answer Review</Divider>
            <div style={{ textAlign: 'left', maxHeight: 350, overflowY: 'auto' }}>
              {(quizResult.breakdown || []).map((item, idx) => (
                <Card
                  key={idx}
                  size="small"
                  style={{
                    marginBottom: 12,
                    borderColor: item.is_correct ? 'rgba(16, 185, 129, 0.35)' : (item.has_answered ? 'rgba(239, 68, 68, 0.35)' : 'var(--border-color, rgba(255, 255, 255, 0.12))'),
                    background: item.is_correct ? 'rgba(16, 185, 129, 0.08)' : (item.has_answered ? 'rgba(239, 68, 68, 0.08)' : 'var(--panel-color, rgba(255, 255, 255, 0.03))'),
                    borderRadius: 10
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Space style={{ marginBottom: 4 }}>
                      {item.is_correct ? <CheckCircleOutlined style={{ color: '#10b981' }} /> : <CloseCircleOutlined style={{ color: item.has_answered ? '#ef4444' : '#94a3b8' }} />}
                      <Text strong style={{ color: 'var(--text-color)' }}>Q{idx + 1}: {item.question_text}</Text>
                    </Space>
                    <Tag color={item.is_correct ? 'green' : (item.has_answered ? 'red' : 'default')}>
                      {item.is_correct ? `+${Math.round(item.marks_earned || item.points || 1)} Marks` : (item.has_answered ? `${Math.round(item.marks_earned || -item.negative_points || 0)} Marks` : 'Unattempted')}
                    </Tag>
                  </div>
                  <div>
                    <Text type="secondary">Your Answer: </Text>
                    <Tag color={item.is_correct ? 'green' : (item.has_answered ? 'red' : 'default')}>
                      {item.options?.[parseInt(item.user_answer)] || item.user_answer || 'No Answer'}
                    </Tag>
                  </div>
                  {!item.is_correct && (
                    <div style={{ marginTop: 4 }}>
                      <Text type="secondary">Correct Answer: </Text>
                      <Tag color="green">{item.options?.[parseInt(item.correct_answer)] || item.correct_answer}</Tag>
                    </div>
                  )}
                </Card>
              ))}
            </div>

            <Button type="primary" size="large" style={{ marginTop: 16 }} onClick={() => setPlayerVisible(false)}>
              Close Assessment
            </Button>
          </div>
        ) : (
          /* ACTIVE QUIZ QUESTION SCREEN */
          <div>
            {timeLeftSeconds !== null && (
              <Alert
                message={
                  <Space>
                    <ClockCircleOutlined style={{ color: '#ef4444' }} />
                    <Text strong style={{ color: '#ef4444' }}>
                      Time Remaining: {Math.floor(timeLeftSeconds / 60)}m {timeLeftSeconds % 60}s
                    </Text>
                  </Space>
                }
                type="warning"
                style={{ marginBottom: 16 }}
              />
            )}

            {quizQuestions.length > 0 && (
              <div>
                <Space style={{ marginBottom: 12, justifyContent: 'space-between', width: '100%' }}>
                  <Text strong style={{ fontSize: 15 }}>
                    Question {currentQIndex + 1} of {quizQuestions.length}
                  </Text>
                  <Progress
                    percent={Math.round(((currentQIndex + 1) / quizQuestions.length) * 100)}
                    size="small"
                    style={{ width: 180 }}
                  />
                </Space>

                {(() => {
                  const currentQ = quizQuestions[currentQIndex];
                  return (
                    <Card style={{ borderRadius: 12, marginBottom: 20, background: 'var(--panel-color, rgba(255, 255, 255, 0.03))', borderColor: 'var(--border-color, rgba(255, 255, 255, 0.12))' }}>
                      {currentQ.question_text && (
                        <Title level={4} style={{ fontSize: 16, marginBottom: 16, color: 'var(--text-color)' }}>
                          {currentQ.question_text}
                        </Title>
                      )}

                      {currentQ.question_image && (
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                          <img
                            src={getFullImageUrl(currentQ.question_image)}
                            alt="Question"
                            style={{ maxHeight: 220, maxWidth: '100%', borderRadius: 8, border: '1px solid var(--border-color, rgba(255, 255, 255, 0.15))' }}
                          />
                        </div>
                      )}

                      <Radio.Group
                        style={{ width: '100%' }}
                        value={userAnswers[currentQ.question_id]}
                        onChange={(e) => setUserAnswers(prev => ({ ...prev, [currentQ.question_id]: e.target.value }))}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {(currentQ.options || []).map((optText, optIdx) => (
                            <Radio
                              key={optIdx}
                              value={String(optIdx)}
                              style={{
                                display: 'flex',
                                width: '100%',
                                padding: '10px 14px',
                                border: userAnswers[currentQ.question_id] === String(optIdx) ? '1px solid #3b82f6' : '1px solid var(--border-color, rgba(255, 255, 255, 0.15))',
                                borderRadius: 8,
                                marginBottom: 6,
                                background: userAnswers[currentQ.question_id] === String(optIdx) ? 'rgba(59, 130, 246, 0.15)' : 'var(--panel-color, rgba(255, 255, 255, 0.02))',
                                color: 'var(--text-color)'
                              }}
                            >
                              <strong>{String.fromCharCode(65 + optIdx)}:</strong> {optText}
                            </Radio>
                          ))}
                        </Space>
                      </Radio.Group>
                    </Card>
                  );
                })()}

                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Button
                    disabled={currentQIndex === 0}
                    onClick={() => setCurrentQIndex(prev => prev - 1)}
                  >
                    Previous Question
                  </Button>

                  {currentQIndex < quizQuestions.length - 1 ? (
                    <Button type="primary" onClick={() => setCurrentQIndex(prev => prev + 1)}>
                      Next Question
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      danger
                      loading={submittingQuiz}
                      onClick={handleFinalSubmitQuiz}
                    >
                      Submit Assessment
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* RESULTS PANEL MODAL (ADMIN / STUDIO / USER) */}
      <Modal
        title={isAdminOrStudio ? `Student Attempts: ${selectedAssessmentForResults?.title}` : `My Attempt Results: ${selectedAssessmentForResults?.title}`}
        open={resultsVisible}
        onCancel={() => setResultsVisible(false)}
        footer={[<Button key="close" type="primary" onClick={() => setResultsVisible(false)}>Close</Button>]}
        width={950}
        destroyOnClose
      >
        {/* Search Bar for Name / Username & Stats */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <Input.Search
            placeholder="Search by student name or username..."
            allowClear
            value={resultsSearchText}
            onChange={(e) => setResultsSearchText(e.target.value)}
            style={{ width: 340 }}
          />
          <Space>
            <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px', borderRadius: 6 }}>
              Total Attempts: {(() => {
                if (!resultsSearchText) return resultsList.length;
                const q = resultsSearchText.trim().toLowerCase();
                return resultsList.filter(rec => {
                  const fullName = (rec.full_name || rec.student_full_name || rec.login_name || '').toLowerCase();
                  const username = (rec.login_username || '').toLowerCase();
                  return fullName.includes(q) || username.includes(q);
                }).length;
              })()}
            </Tag>
          </Space>
        </div>

        <Table
          dataSource={(() => {
            if (!resultsSearchText) return resultsList;
            const q = resultsSearchText.trim().toLowerCase();
            return resultsList.filter(rec => {
              const fullName = (rec.full_name || rec.student_full_name || rec.login_name || '').toLowerCase();
              const username = (rec.login_username || '').toLowerCase();
              return fullName.includes(q) || username.includes(q);
            });
          })()}
          columns={resultsColumns}
          rowKey="attempt_id"
          loading={loadingResults}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} attempts`
          }}
          expandable={{
            expandedRowRender: (rec) => (
              <div style={{
                padding: '12px 16px',
                background: 'var(--panel-color, rgba(255, 255, 255, 0.04))',
                border: '1px solid var(--border-color, rgba(255, 255, 255, 0.1))',
                borderRadius: 8
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--text-color)' }}>
                  Question Responses Breakdown:
                </div>
                {(rec.breakdown || []).map((b, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '8px 12px',
                      marginBottom: 6,
                      borderRadius: 8,
                      background: b.is_correct ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                      border: `1px solid ${b.is_correct ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text-color)' }}>
                        Q{idx + 1}: {b.question_text}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                        Your Choice: <strong>{b.options?.[parseInt(b.user_answer)] || b.user_answer || 'No Answer'}</strong>
                      </div>
                    </div>
                    <Tag color={b.is_correct ? 'green' : 'red'}>
                      {b.is_correct ? <CheckCircleOutlined /> : <CloseCircleOutlined />} {b.marks_earned > 0 ? `+${b.marks_earned} pts` : `${b.marks_earned || 0} pts`}
                    </Tag>
                  </div>
                ))}
              </div>
            )
          }}
        />
      </Modal>

      {/* COPY ASSESSMENT MODAL (ADMIN / STUDIO) */}
      <Modal
        title={`Copy Assessment: ${assessmentToCopy?.title}`}
        open={copyVisible}
        onCancel={() => setCopyVisible(false)}
        onOk={handleExecuteCopy}
        confirmLoading={copying}
        okText="Copy Assessment"
      >
        <Form layout="vertical">
          <Form.Item label="Target Course" required>
            <Select
              showSearch
              optionFilterProp="children"
              value={targetCourseId}
              onChange={handleCourseChangeForCopy}
              placeholder="Select Target Course"
            >
              {allCourses.map(c => (
                <Option key={c.courses_id} value={c.courses_id}>
                  {c.courses_name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Target Module" required>
            <Select
              showSearch
              optionFilterProp="children"
              value={targetModuleId}
              onChange={(val) => setTargetModuleId(val)}
              placeholder="Select Target Module"
              disabled={!targetCourseId}
            >
              {allModules.map(m => (
                <Option key={m.modules_id} value={m.modules_id}>
                  {m.modules_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AssessmentTab;
